import { minutesToMilliseconds } from "date-fns";

import {
	getMeetingsIntegrationsAvailableProviders,
	getMeetingsIntegrationsSettings,
	getUserCalendarIntegrationStatus,
	getUserCalendars,
	getUserVideoConferencingStatus,
} from "../../services/defs/internal-api";

// Marker the backend (clearIntegrationForReconnect) sets on the 422 it returns once it has cleared a
// dead calendar connection (revoked refresh token, or expired with no refresh token). Must stay in
// sync with the value set in the packages/dto meetings-integrations service-action.
const CONNECTION_CLEARED_CALENDAR_ERROR = "CONNECTION_CLEARED";

/**
 * Retry predicate for calendar queries. The only deterministic, non-retryable failure is a calendar
 * connection the backend has already cleared — retrying that never helps and just delays the error.
 * Every other failure (network errors, 5xx, and transient calendar API errors that are also surfaced
 * as 422) is treated as potentially transient and retried up to the default 3 times.
 */
export const shouldRetryCalendarRequest = (failureCount: number, error: unknown): boolean => {
	const calendarErrorType = (error as { data?: { meta?: { calendarErrorType?: string } } })?.data?.meta
		?.calendarErrorType;
	if (calendarErrorType === CONNECTION_CLEARED_CALENDAR_ERROR) {
		return false;
	}
	return failureCount < 3;
};

export const meetingsIntegrationsSettingsQuery = () => ({
	queryKey: ["meetings-integrations-settings"],
	queryFn: getMeetingsIntegrationsSettings,
	staleTime: minutesToMilliseconds(2),
	meta: {
		errorMessage: "There was an error getting meetings integrations settings.",
	},
});

export const userCalendarIntegrationStatusQuery = () => ({
	queryKey: ["user-calendar-integration-status"],
	queryFn: getUserCalendarIntegrationStatus,
	staleTime: minutesToMilliseconds(1),
	meta: {
		errorMessage: "There was an error getting user calendar integration status.",
	},
});

/**
 * Unified query to get user calendars from any provider
 */
export const userCalendarsQuery = (provider: "google" | "outlook") => ({
	queryKey: ["user-calendars", provider],
	queryFn: () => getUserCalendars({ provider }),
	staleTime: minutesToMilliseconds(5), // 5 minutes
	// Don't retry a connection the backend has already cleared; transient failures still retry.
	retry: shouldRetryCalendarRequest,
	meta: {
		errorMessage: "Error Fetching Calendars",
	},
});

export const meetingsIntegrationsAvailableProvidersQuery = () => ({
	queryKey: ["meetings-integrations-available-providers"],
	queryFn: getMeetingsIntegrationsAvailableProviders,
	staleTime: minutesToMilliseconds(10),
	meta: {
		errorMessage: "There was an error fetching available video conferencing providers.",
	},
});

export const userVideoConferencingStatusQuery = () => ({
	queryKey: ["user-video-conferencing-status"],
	queryFn: getUserVideoConferencingStatus,
	staleTime: minutesToMilliseconds(1),
	meta: {
		errorMessage: "There was an error getting your video conferencing connections.",
	},
});
